import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useAuth } from "../context/AuthContext";
import { useConfirm } from "../context/ConfirmContext";
import { api, fileToBase64 } from "../lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import AIAdBuilder from "../components/AIAdBuilder";
import PaymentFlow from "../components/PaymentFlow";
import LoyaltyProgram from "../components/LoyaltyProgram";
import LoyaltyAdmin from "../components/LoyaltyAdmin";
import RaffleTicketAward from "../components/RaffleTicketAward";
import BusinessPoster from "../components/BusinessPoster";
import BusinessQRs from "../components/BusinessQRs";
import EditingPolicyCard from "../components/EditingPolicyCard";
import MenuEditor from "../components/MenuEditor";
import { Store, ImagePlus, Wand2, CreditCard, Gift, Printer, Lock, Trash2, BookOpen, X, QrCode } from "lucide-react";
import { toast } from "sonner";

export default function BusinessDashboard() {
  const { user, refresh } = useAuth();
  const [params, setParams] = useSearchParams();
  const initialTab = params.get("tab") || "anuncio";
  const [tab, setTab] = useState(initialTab);

  const [business, setBusiness] = useState(user?.business || null);
  const [ad, setAd] = useState(null);
  const [showGracePeriodModal, setShowGracePeriodModal] = useState(false);

  useEffect(() => {
    api.get("/business/me").then((r) => setBusiness(r.data)).catch(() => {});
    api.get("/ads/me").then((r) => setAd(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const dismissed = localStorage.getItem("grace_period_modal_dismissed");
    if (!dismissed) {
      setShowGracePeriodModal(true);
    }
  }, []);

  const handleDontShowAgain = () => {
    localStorage.setItem("grace_period_modal_dismissed", "true");
    setShowGracePeriodModal(false);
  };

  const subActive = business?.subscription_active;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <section className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="overline text-[hsl(var(--primary))]">Mi panel</div>
            <h1 className="font-display text-4xl tracking-tight mt-2">{business?.business_name || "Mi negocio"}</h1>
            <div className="text-sm text-muted-foreground mt-1">
              {business?.giro} · CP {business?.postal_code} · {business?.city}
            </div>
          </div>
          <div className={`rounded-full px-4 py-2 text-sm font-medium border ${subActive ? "bg-[#EDE9FE] border-[#7C3AED]/30 text-[#5B21B6]" : "bg-[#F4F4F5] border-zinc-300 text-zinc-700"}`} data-testid="subscription-status">
            {subActive ? "Suscripción activa" : "Suscripción inactiva"}
          </div>
        </div>

        <ExpiryBanner business={business} onGoToPay={() => { setTab("pago"); setParams({ tab: "pago" }); }} />

        <EditingPolicyCard />

        <Tabs value={tab} onValueChange={(v) => { setTab(v); setParams({ tab: v }); }} className="mt-8">
          <TabsList className="bg-white border border-border flex-nowrap" data-testid="dashboard-tabs">
            <TabsTrigger value="anuncio" data-testid="tab-anuncio"><Wand2 className="w-4 h-4 mr-1.5 shrink-0" /><span>Mi anuncio</span></TabsTrigger>
            <TabsTrigger value="lealtad" data-testid="tab-lealtad"><Gift className="w-4 h-4 mr-1.5 shrink-0" /><span>Lealtad</span></TabsTrigger>
            <TabsTrigger value="menu" data-testid="tab-menu"><BookOpen className="w-4 h-4 mr-1.5 shrink-0" /><span>Menú</span></TabsTrigger>
            <TabsTrigger value="cartel" data-testid="tab-cartel"><Printer className="w-4 h-4 mr-1.5 shrink-0" /><span>Cartel</span></TabsTrigger>
            <TabsTrigger value="qrs" data-testid="tab-qrs"><QrCode className="w-4 h-4 mr-1.5 shrink-0" /><span>Mis QR</span></TabsTrigger>
            <TabsTrigger value="perfil" data-testid="tab-perfil"><Store className="w-4 h-4 mr-1.5 shrink-0" /><span>Mi perfil</span></TabsTrigger>
            <TabsTrigger value="pago" data-testid="tab-pago"><CreditCard className="w-4 h-4 mr-1.5 shrink-0" /><span>Pago</span></TabsTrigger>
          </TabsList>

          <TabsContent value="anuncio" className="mt-6">
            {!subActive ? (
              <InactiveCallout onGoToPay={() => { setTab("pago"); setParams({ tab: "pago" }); }} />
            ) : (
              <AIAdBuilder
                business={business}
                ad={ad}
                onAdSaved={(a) => { setAd(a); api.get("/business/me").then((r) => setBusiness(r.data)); }}
                onBusinessSaved={(b) => setBusiness(b)}
              />
            )}
          </TabsContent>

          <TabsContent value="lealtad" className="mt-6">
            <div className="space-y-6">
              {subActive && <RaffleTicketAward business={business} />}
              <LoyaltyProgram business={business} onSaved={(b) => { setBusiness(b); refresh(); }} />
              {business?.loyalty_enabled && subActive && (
                <LoyaltyAdmin business={business} onChange={() => api.get("/business/me").then((r) => setBusiness(r.data))} />
              )}
            </div>
          </TabsContent>

          <TabsContent value="menu" className="mt-6">
            <MenuEditor onGoToQRs={() => { setTab("qrs"); setParams({ tab: "qrs", qr: "menu" }); }} />
          </TabsContent>

          <TabsContent value="cartel" className="mt-6">
            <BusinessPoster business={business} onGoToQRs={() => { setTab("qrs"); setParams({ tab: "qrs", qr: "cartel" }); }} />
          </TabsContent>

          <TabsContent value="qrs" className="mt-6">
            <BusinessQRs
              business={business}
              activeQrFilter={params.get("qr")}
              onClearQrFilter={() => setParams({ tab: "qrs" })}
            />
          </TabsContent>

          <TabsContent value="perfil" className="mt-6">
            <ProfileTab business={business} onSaved={(b) => { setBusiness(b); refresh(); }} />
          </TabsContent>

          <TabsContent value="pago" className="mt-6">
            <PaymentFlow business={business} onSubmitted={() => { refresh(); }} />
          </TabsContent>
        </Tabs>
      </section>
      <Footer />

      {showGracePeriodModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-testid="grace-period-modal">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
            <div className="flex items-start justify-between mb-4">
              <div className="w-12 h-12 rounded-full bg-[#EDE9FE] grid place-items-center">
                <Gift className="w-6 h-6 text-[#7C3AED]" />
              </div>
              <button
                onClick={() => setShowGracePeriodModal(false)}
                className="text-muted-foreground hover:text-foreground"
                data-testid="grace-modal-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <h3 className="font-display text-2xl tracking-tight mb-3">
              ¡Tu directorio está creciendo!
            </h3>
            <p className="text-muted-foreground mb-6">
              Mientras conformamos el directorio completo en tu zona, recibirás hasta <strong className="text-[#7C3AED]">15 días de prórroga sin costo</strong>. Nuestro asesor está integrando más comercios a tu directorio para que tengas más vecinos viendo tus ofertas.
            </p>
            <div className="flex flex-col gap-2">
              <Button
                onClick={() => setShowGracePeriodModal(false)}
                className="btn-primary w-full"
                data-testid="grace-modal-understood"
              >
                Entendido
              </Button>
              <button
                onClick={handleDontShowAgain}
                className="text-sm text-muted-foreground hover:text-foreground underline"
                data-testid="grace-modal-dont-show"
              >
                No volver a mostrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function InactiveCallout({ onGoToPay }) {
  return (
    <div className="bg-white border border-border rounded-2xl p-10 text-center" data-testid="inactive-callout">
      <div className="w-14 h-14 mx-auto rounded-full bg-[hsl(var(--primary))]/10 grid place-items-center">
        <CreditCard className="w-7 h-7 text-[hsl(var(--primary))]" />
      </div>
      <h3 className="font-display text-2xl mt-4 tracking-tight">Activa tu suscripción para crear anuncios</h3>
      <p className="text-muted-foreground mt-2 max-w-md mx-auto">
        Cobra $199 al mes y podrás diseñar tu anuncio con IA. Realiza tu transferencia y sube el comprobante.
      </p>
      <Button className="btn-primary rounded-full mt-6 px-6 py-5" onClick={onGoToPay} data-testid="goto-pay-btn">
        Ir al pago
      </Button>
    </div>
  );
}

function ExpiryBanner({ business, onGoToPay }) {
  if (!business?.subscription_expires_at) return null;
  const exp = new Date(business.subscription_expires_at);
  const days = Math.ceil((exp.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  if (days > 7) return null;
  const isExpired = days < 0 || !business.subscription_active;
  const isToday = days === 0;
  const tone = isExpired ? "rose" : days <= 3 ? "amber" : "violet";
  const cls = {
    rose: "bg-rose-50 border-rose-200 text-rose-900",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    violet: "bg-[#F5F3FF] border-[#DDD6FE] text-[#5B21B6]",
  }[tone];
  let msg;
  if (isExpired) msg = "Tu suscripción venció. Renueva para volver a aparecer en el directorio.";
  else if (isToday) msg = "Tu suscripción vence HOY. Renueva $199 MXN para mantener tu anuncio activo.";
  else msg = `Tu suscripción vence en ${days} día${days === 1 ? "" : "s"}. Renueva $199 MXN.`;

  return (
    <div className={`mt-6 border rounded-2xl px-5 py-4 flex items-center justify-between gap-4 flex-wrap ${cls}`} data-testid="expiry-banner">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-full bg-white/60 grid place-items-center shrink-0">
          <CreditCard className="w-4 h-4" />
        </div>
        <div className="text-sm font-medium">{msg}</div>
      </div>
      <Button onClick={onGoToPay} className="btn-primary rounded-full px-5" data-testid="expiry-renew-btn">
        Renovar ahora
      </Button>
    </div>
  );
}

function ProfileTab({ business, onSaved }) {
  const confirm = useConfirm();
  const [form, setForm] = useState(business || {});
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onLogo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const b64 = await fileToBase64(f);
    set("logo_base64", b64);
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/business/me", form);
      onSaved && onSaved(data);
      toast.success("Datos actualizados");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid md:grid-cols-3 gap-6">
      <div className="md:col-span-2 bg-white border border-border rounded-2xl p-6 space-y-4">
        <h3 className="font-display text-2xl tracking-tight">Datos del comercio</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Nombre"><Input value={form.business_name || ""} onChange={(e) => set("business_name", e.target.value)} data-testid="profile-name" /></Field>
          <Field label="Giro"><Input value={form.giro || ""} onChange={(e) => set("giro", e.target.value)} data-testid="profile-giro" /></Field>
          <Field label="Domicilio"><Input value={form.address || ""} onChange={(e) => set("address", e.target.value)} data-testid="profile-address" /></Field>
          <Field label="Código Postal"><Input value={form.postal_code || ""} onChange={(e) => set("postal_code", e.target.value)} data-testid="profile-cp" /></Field>
          <Field label="Colonia"><Input value={form.colonia || ""} onChange={(e) => set("colonia", e.target.value)} data-testid="profile-colonia" /></Field>
          <Field label="Ciudad"><Input value={form.city || ""} onChange={(e) => set("city", e.target.value)} data-testid="profile-city" /></Field>
          <Field label="Estado"><Input value={form.state || ""} onChange={(e) => set("state", e.target.value)} data-testid="profile-state" /></Field>
          <Field label="WhatsApp"><Input value={form.whatsapp || ""} onChange={(e) => set("whatsapp", e.target.value)} data-testid="profile-whatsapp" /></Field>
          <Field label="Horario" className="md:col-span-2"><Input value={form.horario || ""} onChange={(e) => set("horario", e.target.value)} placeholder="Ej: Lun-Vie 9:00-18:00, Sáb 10:00-14:00" data-testid="profile-horario" /></Field>
        </div>
        <Button onClick={save} disabled={busy} className="btn-primary rounded-full px-6 py-5" data-testid="profile-save-btn">
          {busy ? "Guardando…" : "Guardar cambios"}
        </Button>
      </div>

      <div className="space-y-6">
        <ChangePasswordCard />
        <div className="bg-white border border-border rounded-2xl p-6">
        <h3 className="font-display text-2xl tracking-tight">Logo</h3>
        <div className="mt-4 aspect-square rounded-xl border border-dashed border-input grid place-items-center overflow-hidden bg-[#FAFAFA]">
          {form.logo_base64 ? (
            <img src={form.logo_base64} alt="logo" className="w-full h-full object-cover" />
          ) : (
            <div className="text-sm text-muted-foreground p-6 text-center">
              Sube el logo de tu comercio
            </div>
          )}
        </div>
        <label className="mt-4 flex items-center justify-center gap-2 border border-input rounded-md py-2.5 cursor-pointer hover:bg-muted">
          <ImagePlus className="w-4 h-4" />
          <span className="text-sm">Subir logo</span>
          <input type="file" accept="image/*" className="hidden" onChange={onLogo} data-testid="logo-upload-input" />
        </label>
        {form.logo_base64 && (
          <button
            onClick={async () => {
              if (!await confirm("¿Eliminar el logo? Esta acción no se puede deshacer.")) return;
              try {
                const { data } = await api.delete("/business/me/logo");
                set("logo_base64", null);
                onSaved && onSaved(data);
                toast.success("Logo eliminado");
              } catch {
                toast.error("Error al eliminar logo");
              }
            }}
            className="mt-2 w-full flex items-center justify-center gap-2 text-sm text-rose-500 hover:text-rose-700 py-2"
            data-testid="logo-delete-btn"
          >
            <Trash2 className="w-4 h-4" />
            Eliminar logo
          </button>
        )}
        </div>
      </div>
    </div>
  );
}

function ChangePasswordCard() {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (form.new_password !== form.confirm) { toast.error("Las contraseñas no coinciden"); return; }
    if (form.new_password.length < 6) { toast.error("La nueva contraseña debe tener al menos 6 caracteres"); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success("Contraseña actualizada");
      setForm({ current_password: "", new_password: "", confirm: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al cambiar contraseña");
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-border rounded-2xl p-6 space-y-4" data-testid="change-password-card">
      <div className="flex items-center gap-2">
        <Lock className="w-4 h-4 text-muted-foreground" />
        <h3 className="font-display text-xl tracking-tight">Cambiar contraseña</h3>
      </div>
      <Field label="Contraseña actual">
        <Input type="password" value={form.current_password} onChange={(e) => set("current_password", e.target.value)} data-testid="pwd-current" />
      </Field>
      <Field label="Nueva contraseña">
        <Input type="password" value={form.new_password} onChange={(e) => set("new_password", e.target.value)} data-testid="pwd-new" />
      </Field>
      <Field label="Confirmar nueva contraseña">
        <Input type="password" value={form.confirm} onChange={(e) => set("confirm", e.target.value)} data-testid="pwd-confirm" />
      </Field>
      <Button onClick={save} disabled={busy || !form.current_password || !form.new_password} className="btn-primary rounded-full px-6" data-testid="pwd-save-btn">
        {busy ? "Guardando…" : "Cambiar contraseña"}
      </Button>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <Label className="text-sm">{label}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);
